import { PmsType } from "@/types/hotel";

export interface PmsProviderOption {
  type: string;
}

export enum SyncJobType {
  FULL = "FULL",
  INCREMENTAL = "INCREMENTAL",
  SELECTIVE = "SELECTIVE",
}

export enum SyncJobStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

export enum SyncEntityType {
  HOTEL = "HOTEL",
  ROOM_TYPE = "ROOM_TYPE",
  ROOM = "ROOM",
  GUEST = "GUEST",
  RESERVATION = "RESERVATION",
  PRODUCT = "PRODUCT",
  SERVICE_CATEGORY = "SERVICE_CATEGORY",
  SERVICE = "SERVICE",
  RATE_PLAN = "RATE_PLAN",
  RATE = "RATE",
  FOLIO = "FOLIO",
}

export interface TriggerSelectiveSyncRequest {
  entities: SyncEntityType[];
}

export enum ReconciliationHealth {
  HEALTHY = "HEALTHY",
  NEEDS_ATTENTION = "NEEDS_ATTENTION",
  CRITICAL = "CRITICAL",
}

export interface PmsSettings {
  id: string;
  hotelId: string;
  provider: string;
  enabled: boolean;
  pmsHotelId?: string;
  baseUrl?: string;
  pollIntervalMinutes: number;
  lastInboundAt?: string;
  lastOutboundAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CredentialsStatus {
  hasCredentials: boolean;
  pmsHotelId?: string;
  provider?: string;
  baseUrl?: string;
  hasAdvancedConfig?: boolean;
}

export type OperaGrantType = "client_credentials" | "password";
export type OperaTransport = "polling" | "streaming";

// Stored server-side as one encrypted blob; secrets are never returned, only
// the hasAdvancedConfig flag. Every save replaces the whole object.
export interface OperaAdvancedConfig {
  appKey: string;
  clientId: string;
  extSystemCode: string;
  grantType?: OperaGrantType;
  enterpriseId?: string;
  scope?: string;
  username?: string;
  password?: string;
  transport?: OperaTransport;
  chainCode?: string;
  wssUrl?: string;
  cashierId?: number;
}

export interface UpdateMewsCredentialsRequest {
  provider: PmsType.MEWS;
  apiToken: string;
  clientToken?: string;
  clientName?: string;
  webhookSecret?: string;
  baseUrl?: string;
  pmsHotelId?: string;
}

// provider: "OPERA" is what exempts the request from the apiToken requirement,
// so it must be present on every OPERA credentials PUT.
export interface UpdateOperaCredentialsRequest {
  provider: PmsType.OPERA;
  baseUrl: string;
  pmsHotelId: string;
  advancedConfig: OperaAdvancedConfig;
  clientToken?: string;
}

export type UpdatePmsCredentialsRequest =
  | UpdateMewsCredentialsRequest
  | UpdateOperaCredentialsRequest;

export interface UpdatePmsSettingsRequest {
  pollIntervalMinutes?: number;
  baseUrl?: string;
  pmsHotelId?: string;
}

export interface EnablePmsRequest {
  pollIntervalMinutes?: number;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
}

export interface SyncJobProgress {
  entityType: SyncEntityType;
  processed: number;
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}

export interface SyncJob {
  id: string;
  hotelId: string;
  type: SyncJobType;
  status: SyncJobStatus;
  startedAt?: string;
  completedAt?: string;
  progress: SyncJobProgress[];
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SyncEventStats {
  pending: number;
  succeeded: number;
  failed: number;
  retrying: number;
}

export interface PmsRecentActivity {
  windowHours: number;
  reservations: number;
  rooms: number;
}

export interface SyncStats {
  eventStats: SyncEventStats;
  lastInboundAt?: string;
  lastOutboundAt?: string;
  recentActivity?: PmsRecentActivity;
}

export interface RecentFailure {
  eventType: string;
  error: string;
  attempts: number;
  createdAt: string;
}

export interface RetryStats {
  pendingCount: number;
  retryingCount: number;
  failedCount: number;
  recentFailures: RecentFailure[];
}

export interface EntitySummary {
  entityType: SyncEntityType;
  mappedCount: number;
  status: ReconciliationHealth;
}

export interface ReconciliationStatus {
  hotelId: string;
  enabled: boolean;
  provider: string;
  hasDrift: boolean;
  entitySummaries: EntitySummary[];
  lastReconciliation?: string;
  driftCheckedAt?: string | null;
}

export type SyncJobsSortField = "createdAt" | "status";
export type SyncJobsSortDirection = "asc" | "desc";

export interface SyncJobsFilters {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  status?: SyncJobStatus;
  type?: SyncJobType;
  sortBy?: SyncJobsSortField;
  sortDirection?: SyncJobsSortDirection;
}

export interface PaginatedSyncJobs {
  items: SyncJob[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export type SyncJobsExportFormat = "csv" | "xlsx";

export interface SyncJobsExportParams
  extends Omit<SyncJobsFilters, "page" | "pageSize"> {
  format: SyncJobsExportFormat;
}
